import type { ThemeOptions, Theme } from '@mui/material/styles'
import { lightPalette, darkPalette } from './palette'

/**
 * Component overrides for MUI components.
 * Uses theme tokens from palette instead of hard-coded values.
 * Theme-aware functions are used where values differ between light and dark themes.
 */
export const componentOverrides: ThemeOptions['components'] = {
    MuiButton: {
        styleOverrides: {
            root: {
                textTransform: 'none',
                borderRadius: 8,
            },
        },
    },
    MuiCard: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }) => ({
                borderRadius: 12,
                boxShadow:
                    theme.palette.mode === 'dark'
                        ? darkPalette.shadow.card
                        : lightPalette.shadow.card,
            }),
        },
    },
    MuiTextField: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }) => {
                const palette = theme.palette.mode === 'dark' ? darkPalette : lightPalette
                return {
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: palette.border.default,
                        },
                        '&:hover fieldset': {
                            borderColor: palette.border.hover,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: palette.border.focused,
                        },
                    },
                    '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                            color: palette.border.focused,
                        },
                    },
                }
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }) => {
                const palette = theme.palette.mode === 'dark' ? darkPalette : lightPalette
                return {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.border.default,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.border.hover,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.border.focused,
                    },
                }
            },
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: ({ theme }: { theme: Theme }) => {
                const palette = theme.palette.mode === 'dark' ? darkPalette : lightPalette
                return {
                    '&.MuiInputLabel-outlined': {
                        '&.Mui-focused': {
                            color: palette.border.focused,
                        },
                        '&.MuiInputLabel-shrink': {
                            backgroundColor: palette.inputLabel.background,
                            padding: '0 4px',
                        },
                    },
                }
            },
            outlined: ({ theme }: { theme: Theme }) => {
                const palette = theme.palette.mode === 'dark' ? darkPalette : lightPalette
                return {
                    '&.Mui-focused': {
                        color: palette.border.focused,
                    },
                    '&.MuiInputLabel-shrink': {
                        backgroundColor: palette.inputLabel.background,
                        padding: '0 4px',
                    },
                }
            },
        },
    },
}
